<template>
  <div class="table-container" v-resize="calculateTableHeight">
    <div class="contacts-search" v-if="!hideSearch">
      <div class="d-flex pa-4">
        <search-toggle-field
          v-model="search"
          placeholder="Find Contacts"
          alwaysOpen
          large
          :delay="500"
        />
        <v-btn
          color="appMain"
          class="ml-4"
          :class="{ 'no-min-width': isMobile }"
          @click="toggleFilters"
        >
          <v-icon color="light" v-if="isMobile">
            {{ showFilters ? 'filter_list_off' : 'filter_list' }}
          </v-icon>
          <template v-else>
            {{ showFilters ? 'Remove Filter' : 'Filter' }}
          </template>
        </v-btn>
        <v-btn
          v-if="showSelectedButton"
          color="appMain"
          class="ml-4"
          @click="toggleSelectedList"
        >
          {{ showSelected ? 'All' : 'Selected' }}
        </v-btn>
      </div>
      <div class="pa-4 pt-0" v-if="showFilters">
        <group-filter-row
          type="attributes"
          :preparedFields="attributesFields"
          :filter="filterField"
          @refresh="refreshFilterField"
          tableFilter
        />
      </div>
    </div>

    <v-data-table
      ref="table"
      class="styled-table contacts-table"
      item-key="con_id"
      fixed-header
      :height="tableHeight"
      :class="{ 'mr-4 ml-4': !insideModal && !hideSearch }"
      :headers="tableHeaders"
      :items="showSkeleton ? skeletonRows : visibleContacts"
      :options.sync="options"
      :server-items-length="serverItemsLength"
      :footer-props="tableFooterProps"
      :show-select="showSelect"
      :mobile-breakpoint="0"
      :loading="isContactsLoading"
      v-model="selected"
      @input="$emit('input', selected)"
      @click:row="editContact"
    >
      <template v-if="showSkeleton" v-slot:item="{ headers }">
        <tr>
          <td
            v-for="header in headers"
            :key="header.value"
            :class="header.cellClass"
            :style="{ minWidth: `${columnWidths[header.value] || 0}px` }"
          >
            <div class="contacts-table__skeleton" />
          </td>
        </tr>
      </template>

      <template v-slot:no-data>
        <div class="hint-block ma-3">
          <v-icon color="appMain" class="mb-4">info</v-icon>

          <contact-not-found
            v-if="isFiltersSet"
            :showAddButton="showAddContactButton"
            :search="filters.name"
            @created="selectCreatedContact"
          />

          <template v-else>
            <p>
              You do not have any contacts added to your account. Please add or
              import contacts to your account, then add them to this campaign.
            </p>
            <smart-tooltip
              type="contacts-an-overview"
              category="contacts"
              link
              title=" Learn about adding contacts"
            />
          </template>
        </div>
      </template>

      <template v-slot:body.prepend>
        <tr v-if="showSelectedButton && showSelected">
          <td colspan="6">
            <div class="table-message">
              <template v-if="showSelected">
                You're currently viewing only selected contacts
                <v-btn
                  class="ml-auto"
                  text
                  color="primary"
                  @click="toggleSelectedList"
                  >Show All</v-btn
                >
              </template>
            </div>
          </td>
        </tr>
      </template>

      <template v-slot:item.data-table-select="{ item, isSelected, select }">
        <v-simple-checkbox
          v-if="isUnselectable(item)"
          :value="true"
          disabled
          readonly
          color="primary"
          class="table__checkbox"
        />

        <v-simple-checkbox
          v-else
          :value="isSelected"
          color="primary"
          class="table__checkbox"
          @input="select($event)"
        />
      </template>

      <template
        v-for="header in tableHeaders"
        v-slot:[`item.${header.value}`]="{ item }"
      >
        <slot :name="[`item.${header.value}`]" :item="item">
          <contacts-table-cell
            :key="item.con_id + header.value"
            :contactId="item.con_id"
            :field="header.value"
            :fieldValue="getFieldValue(item[header.value], header.value)"
            :fieldSchema="getFieldSchema(header.value)"
            :contactName="getContactName(item)"
          />
        </slot>
      </template>

      <template v-slot:item.name="{ item }">
        <router-link
          class="text-decoration-none"
          :to="getContactPath(item)"
          :class="{ 'pointer-events-none': hideControls && !editable }"
        >
          {{ getContactName(item) }}
        </router-link>
      </template>

      <template v-slot:item.action="{ item }">
        <contact-controls :contact="item" />
      </template>
    </v-data-table>

    <contact-action-dialog />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions, mapMutations } from 'vuex';

import { formatDateFull, formatFieldValue } from '@/filters/fieldFormatters';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';

import ContactControls from './ContactControls';
import ContactActionDialog from '@/views/contacts/contact/ContactActionDialog';
import ContactNotFound from '@/views/contacts/components/ContactNotFound';
import ContactsTableCell from '@/views/contacts/components/ContactsTableCell.vue';

import GroupFilterRow from '@/views/groups/form/GroupFilterRow';
import SearchToggleField from '@/components/basic/search-toggle-field/SearchToggleField';
import SmartTooltip from '@/components/basic/smart-tooltip/SmartTooltip';

export default {
  name: 'ContactsTable',
  components: {
    ContactControls,
    ContactActionDialog,
    ContactNotFound,
    ContactsTableCell,

    GroupFilterRow,
    SearchToggleField,
    SmartTooltip,
  },
  props: {
    outerFilters: {},
    campaignId: String,
    insideCampaign: Boolean,
    triggerId: String,
    insideModal: Boolean,
    hideControls: Boolean,
    hideSearch: Boolean,
    shortHeader: Boolean,
    showSelectedButton: Boolean,
    showAddContactButton: Boolean,
    ignoreMargin: Boolean,
    showSelect: {
      type: Boolean,
      default: true,
    },
    progressTable: Boolean,
    editable: Boolean,
    archived: String,
    customHeaders: {
      type: Array,
      default: () => [],
    },
    teamFilterOff: Boolean,
    disableSelectionForContacts: {
      type: Array,
      default: () => [],
    },
    /**
     * Explicit list of field names to request from the contacts API.
     * first_name and last_name are always included automatically.
     * When empty, falls back to team-based fields or default behavior.
     */
    requestFields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      filters: {
        name: '',
      },
      filterField: {
        field: '',
        operator: '',
        value: '',
      },
      options: {
        groupBy: [],
        groupDesc: [],
        sortBy: ['created_at'],
        sortDesc: [false],
        itemsPerPage: 25,
        multiSort: false,
        mustSort: false,
        page: 1,
      },
      tableHeight: 0,
      selected: [],
      defaultLimit: 50,
      showSelected: false,
      showFilters: false,
      loadingContacts: [],
      loadingChunks: false,
      columnWidths: {},
      updateTimeout: null,
      progressTableFields: [],
      shortTableFields: ['name', 'mobile', 'email', 'created_at'],
    };
  },

  computed: {
    ...mapGetters(['contacts', 'contactsLoading', 'contactsInfo']),
    ...mapGetters('teams', ['currentTeam']),
    ...mapGetters('org', ['fieldSections']),
    ...mapGetters('fields', {
      attributesFields: 'attributesFields',
      defaultFields: 'defaultFields',
      fieldsLoaded: 'loaded',
      getSingleField: 'getSingleField',
    }),

    // Cached per column so cells get stable schema objects and don't re-render on every table update.
    fieldSchemas() {
      return Object.fromEntries(
        this.tableHeaders.map(({ value }) => [
          value,
          this.getSingleField(value),
        ])
      );
    },

    search: {
      get() {
        return this.filters.name;
      },
      set(value) {
        this.filters.name = value;
        this.searchContacts();
      },
    },

    isContactsLoading() {
      return (
        this.contactsLoading ||
        this.loadingChunks ||
        !this.fieldsLoaded ||
        this.loadingContacts.length > 0
      );
    },

    tableHeaders() {
      return this.buildTableHeaders();
    },

    tableFooterProps() {
      return {
        itemsPerPageOptions: [25],
        showCurrentPage: true,
        pageText: this.getPaginationText,
      };
    },

    filteredSelectedContacts() {
      const search = (this.filters.name || '').toLowerCase();
      const contacts = this.selected || [];
      return contacts.filter((i) =>
        this.compareStrings(this.getContactName(i), search)
      );
    },

    itemsLength() {
      if (this.showSelected) return this.selected.length;
      return this.contactsInfo.count || this.visibleContacts.length;
    },

    // count is gone right after an all/archived switch; 0 makes Vuetify show "No matching records" over the skeleton.
    serverItemsLength() {
      if (!this.showSkeleton) return this.itemsLength;
      return Math.max(this.itemsLength, this.skeletonRows.length);
    },

    showSkeleton() {
      return this.isContactsLoading && !this.visibleContacts.length;
    },

    // Plain divs, only as many rows as fit: wide tables have 100+ columns, so skeleton components cost seconds.
    skeletonRows() {
      const length = Math.ceil(this.tableHeight / 56) || 10;
      return Array.from({ length }, (_, i) => ({
        con_id: `skeleton-${i}`,
      }));
    },

    visibleContacts() {
      const { page, itemsPerPage } = this.options;
      const contacts = this.showSelected
        ? this.filteredSelectedContacts
        : this.contacts;
      return contacts.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    },

    isFiltersSet() {
      return (
        this.filters.name ||
        this.outerFilters ||
        this.showSelected ||
        Object.keys(this.filters).length > 2
      );
    },

    currentFilters() {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const contactsParams = {
        filters: cloneDeep(this.filters),
        page: Math.ceil((page * itemsPerPage) / this.defaultLimit),
      };

      if (sortBy && sortBy.length) {
        contactsParams.sort = { [sortBy[0]]: sortDesc[0] ? 1 : -1 };
      }

      if (!contactsParams.filters.name) {
        delete contactsParams.filters.name;
      }

      return contactsParams;
    },

    // Determine which fields to request from the contacts API.
    // first_name and last_name are always included so the Name column works.
    visibleContactFields() {
      const REQUIRED_FIELDS = ['first_name', 'last_name'];

      // Explicit requestFields prop takes priority (e.g. from custom views)
      if (this.requestFields.length) {
        return [...new Set([...REQUIRED_FIELDS, ...this.requestFields])];
      }

      // Team-based field restrictions
      const contactFields = this.currentTeam?.contact_fields;
      if (!contactFields?.length || this.customHeaders.length) return [];

      return [...REQUIRED_FIELDS, ...contactFields];
    },

    getPaginationText() {
      const { page, itemsPerPage } = this.options;
      const totalCount = this.itemsLength;
      if (!totalCount) return '';

      const chunkStartCount = (page - 1) * itemsPerPage + 1;
      const chunkEndCount = Math.min(page * itemsPerPage, totalCount);

      return `${chunkStartCount}-${chunkEndCount} of ${totalCount}`;
    },
  },
  watch: {
    // Runs before re-render, so the previous page's columns are still in the DOM to measure.
    showSkeleton(show) {
      if (!show) return;

      const ths = this.$refs.table.$el.querySelectorAll('thead th');
      this.columnWidths = Object.fromEntries(
        this.$refs.table.computedHeaders.map(({ value }, i) => [
          value,
          ths[i]?.offsetWidth,
        ])
      );
    },
    options: {
      deep: true,
      handler(newOptionsState, oldOptionsState) {
        if (!oldOptionsState?.page || isEqual(newOptionsState, oldOptionsState))
          return;

        this.loadMissingChunks();
        this.savePageState(newOptionsState.page);
      },
    },
    'options.sortDesc': {
      handler(newVal, oldVal) {
        this.sortChangeHandler(newVal, oldVal);
      },
    },
    'options.sortBy': {
      handler(newVal, oldVal) {
        this.sortChangeHandler(newVal, oldVal);
      },
    },
    outerFilters: {
      deep: true,
      handler(newVal) {
        if (this.updateTimeout) {
          clearTimeout(this.updateTimeout);
        }

        this.updateTimeout = setTimeout(() => {
          let currentFilter = cloneDeep(this.filters);
          delete currentFilter['campaigns'];
          if (isEqual(newVal, currentFilter)) return;

          this.initContacts();
        }, 500);
      },
    },
    visibleContacts: {
      handler(newVal, oldVal) {
        if (this.progressTable && !isEqual(newVal, oldVal)) {
          this.loadContactsProgress();
        }
      },
    },
  },
  methods: {
    ...mapMutations(['setContacts', 'setContactsInfo']),
    ...mapActions(['loadContacts', 'loadContactMetric']),
    ...mapActions('fields', { loadFields: 'load' }),
    ...mapActions('users', ['loadUsers']),

    isUnselectable({ con_id }) {
      if (!this.disableSelectionForContacts.length || !con_id) return false;
      return this.disableSelectionForContacts.includes(con_id);
    },

    getFieldSchema(field) {
      return this.fieldSchemas[field] ?? this.getSingleField(field);
    },

    getFieldType(field) {
      const fieldSchema = this.getFieldSchema(field);
      return fieldSchema?.type;
    },

    getFieldValue(value, field) {
      const fieldSchema = this.getFieldSchema(field);
      const fieldType = fieldSchema?.type;

      if (fieldType === 'time' || fieldType === 'date') {
        return formatDateFull(value, field);
      }

      if (['points', 'maxPoints'].includes(field)) {
        return (value || 0).toLocaleString('en-US');
      }

      if (['progress', 'grade'].includes(field)) {
        const isNumeric = typeof value === 'number';
        return isNumeric ? `${Math.round(value * 100)}%` : '';
      }

      if (fieldSchema?.format) {
        return formatFieldValue(value, fieldType, {
          fieldName: field,
          format: fieldSchema.format,
          context: 'list',
        });
      }

      return value;
    },

    isFieldVisible(field) {
      const { active, label, name } = field;
      if (
        !active ||
        label === 'name' ||
        ['campaigns', 'created_at'].includes(name)
      ) {
        return false;
      }
      if (!this.visibleContactFields.length) return true;
      return this.visibleContactFields.includes(name);
    },

    customFieldSort(fields) {
      const sectionOrder = (this.fieldSections || []).map((s) => s.slug);

      return fields.sort((a, b) => {
        const aIndex = sectionOrder.indexOf(a.section || '');
        const bIndex = sectionOrder.indexOf(b.section || '');

        return aIndex !== bIndex
          ? (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex)
          : (a.order || 0) - (b.order || 0);
      });
    },

    buildTableHeaders() {
      if (!this.fieldsLoaded) return [];

      let headers = [
        {
          text: 'Name',
          value: 'name',
          cellClass: 'fixed',
          class: 'fixed',
          width: 140,
          sortable: true,
        },
        {
          text: 'Created At',
          value: 'created_at',
          sortable: true,
          width: 160,
        },
      ];

      if (!this.hideControls) {
        headers.unshift({
          text: '',
          value: 'action',
          cellClass: 'cell-action fixed pl-2 pr-0',
          class: 'fixed',
          width: 50,
          sortable: false,
        });
      }

      if (this.defaultFields) {
        // Sort fields by section first, then by order within section
        const sortedFields = this.customFieldSort([...this.defaultFields]);

        headers = sortedFields.reduce((acc, field) => {
          if (this.isFieldVisible(field)) {
            acc.push({
              text: field.label,
              value: field.name,
              sortable: true,
              iskey: field.iskey,
              name: field.name,
              width: field.type === 'time' ? 220 : undefined,
            });
          }

          return acc;
        }, headers);
      }

      if (this.shortHeader) {
        headers = headers.filter((i) => {
          return this.shortTableFields.includes(i.value) || i.iskey;
        });
      }

      if (this.customHeaders.length) {
        headers = this.customHeaders;
      }

      return headers;
    },

    selectCreatedContact(contact) {
      this.selected.push(contact);
    },

    // Table page and API page differ (25 rows per page vs 50 per request), so they're stored separately.
    savePageState(page) {
      const { filters } = this.contactsInfo;
      if (!filters || this.contactsInfo.page === page) return;

      this.setContactsInfo({
        ...this.contactsInfo,
        page,
        filters: { ...filters, page: this.currentFilters.page },
      });
    },

    sortChangeHandler(newVal, oldVal) {
      if (!isEqual(newVal, oldVal) && oldVal !== undefined) {
        this.searchContacts();
      }
    },

    loadContactsProgress() {
      this.visibleContacts.forEach((contact) => {
        let contactId = contact.con_id;
        if (
          contact.progress !== undefined ||
          this.loadingContacts.includes(contactId)
        )
          return;

        this.loadingContacts.push(contactId);

        this.loadContactMetric({
          resource: 'campaign_progress',
          filters: {
            con_id: contact.con_id,
            campaign_id: this.campaignId,
          },
        }).then(() => {
          this.loadingContacts = this.loadingContacts.filter(
            (i) => i !== contactId
          );
        });
      });
    },

    searchContacts() {
      this.clearContacts();
      this.loadContactsChunkThrottle();
    },

    toggleSelectedList() {
      this.options.page = 1;
      this.showSelected = !this.showSelected;
      if (this.showSelected) {
        this.filters.name = '';
      }
    },

    clearContacts() {
      if (isEqual(this.currentFilters, this.contactsInfo.filters)) return;

      this.options.page = 1;
      this.setContacts([]);
    },

    setTableOptions() {
      const storedFilters = this.contactsInfo.filters;

      if (storedFilters) {
        if (this.contactsInfo.page) {
          this.options.page = this.contactsInfo.page;
        }

        // set sort from store
        // table sort = ts / server sort = ss
        // ts = sortDesc:[false] | ss = sort: -1
        // ts = sortDesc:[true]  | ss = sort: 1
        // ts = sortDesc[]       | ss =
        if (storedFilters.sort) {
          this.options.sortBy = Object.keys(storedFilters.sort);
          this.options.sortDesc = [
            storedFilters.sort[this.options.sortBy] === 1,
          ];
        } else {
          this.options.sortBy = [];
          this.options.sortDesc = [];
        }
      }

      if (this.outerFilters) {
        this.filters = {
          name: this.filters.name || '',
          ...cloneDeep(this.outerFilters),
        };
      }

      if (this.campaignId) {
        let filter = this.insideCampaign ? '$eq' : '$ne';
        this.filters['campaigns'] = { [filter]: this.campaignId };
      }
    },

    async initContacts() {
      // assign stored filters
      this.setTableOptions();
      await this.$nextTick();

      // clear table data if filters changed
      this.clearContacts();

      this.loadContactsChunkThrottle();
    },

    loadContactsChunkThrottle: throttle(function () {
      this.loadContactsChunk();
    }, 1000),

    loadContactsChunk() {
      const { filters, archived } = this.contactsInfo;
      const isEqualFilters = isEqual(this.currentFilters, filters);
      const isEqualArchived = (this.archived || false) === (archived || false);

      if (isEqualFilters && isEqualArchived) {
        return;
      }

      this.loadContacts({
        filters: this.currentFilters,
        fields: this.visibleContactFields,
        archived: this.archived,
        format: 'v2',
        teamFilterOff: this.teamFilterOff,
      });
    },

    // Load pages one at a time up to the current page so fast paging can't leave gaps or misorder chunks.
    async loadMissingChunks() {
      if (this.loadingChunks) return;
      this.loadingChunks = true;

      try {
        while (
          this.contacts.length <
            this.options.page * this.options.itemsPerPage &&
          this.contacts.length < this.contactsInfo.count
        ) {
          const loadedCount = this.contacts.length;

          await this.loadContacts({
            filters: {
              ...this.currentFilters,
              page: Math.ceil(loadedCount / this.defaultLimit) + 1,
            },
            fields: this.visibleContactFields,
            archived: this.archived,
            format: 'v2',
            teamFilterOff: this.teamFilterOff,
          });

          if (this.contacts.length === loadedCount) break;
        }
      } finally {
        this.loadingChunks = false;
      }
    },

    forceReload() {
      this.options.page = 1;
      this.setContacts([]);
      this.loadContacts({
        filters: this.currentFilters,
        fields: this.visibleContactFields,
        archived: this.archived,
        format: 'v2',
        teamFilterOff: this.teamFilterOff,
      });
    },

    getContactPath(contact) {
      return {
        name: 'contact-edit',
        params: {
          id: contact.con_id,
        },
      };
    },

    editContact(contact) {
      if (this.progressTable) {
        this.$emit('show', contact);
      }

      if (this.hideControls && !this.editable) {
        return false;
      }

      this.$router.push(this.getContactPath(contact));
    },

    compareStrings(fullString, search) {
      return fullString.toLowerCase().includes(search);
    },

    toggleFilters() {
      this.showFilters = !this.showFilters;
      if (!this.showFilters) {
        this.removeFilterField();
        this.searchContacts();
      }
      setTimeout(() => {
        this.calculateTableHeight();
      }, 100);
    },

    removeFilterField() {
      const outerKeys = this.outerFilters ? Object.keys(this.outerFilters) : [];
      for (let key in this.filters) {
        if (key !== 'campaigns' && key !== 'name' && !outerKeys.includes(key)) {
          Vue.delete(this.filters, key);
        }
      }
    },

    async refreshFilterField() {
      this.removeFilterField();

      const filter = this.filterField;
      if (filter.field && filter.operator) {
        Vue.set(this.filters, filter.field.value, {
          [filter.operator.operator || filter.operator.value]: filter.value,
        });
      }

      this.searchContacts();
    },

    clearContactsTable() {
      this.removeFilterField();
      this.selected = [];
      this.showSelected = false;
    },

    async calculateTableHeight() {
      if (this.insideModal) await this.$nextTick();

      // Use this.$el (the component root .table-container) instead of
      // document.querySelector so each instance measures its own DOM.
      const tableContainer = this.$el;
      const tableFooterHeight = 39;
      if (!tableContainer) return;

      let tableHeight = tableContainer.clientHeight - tableFooterHeight;
      if (!this.hideSearch) {
        const searchBar = tableContainer.querySelector('.contacts-search');
        if (!searchBar) return;
        tableHeight -= searchBar.clientHeight;

        if (!this.progressTable && !this.ignoreMargin) {
          tableHeight -= 45;
        }
      }

      this.tableHeight = tableHeight;
    },
    loadResources() {
      this.loadFields({ checkLoaded: true });
      this.loadUsers();
    },
  },
  mounted() {
    this.loadResources();
    this.initContacts();
    this.calculateTableHeight();
  },
};
</script>

<style lang="less">
.contacts-table {
  position: relative;
  .contacts-table__skeleton {
    height: 12px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.12);
  }
  .cell-action .contacts-table__skeleton {
    max-width: 14px;
    margin: 0 auto;
  }
  table {
    background-color: #fff;
    th {
      text-transform: capitalize;
    }
  }
}

.groups-modal {
  .contacts-table {
    height: 100%;
    .v-data-table__wrapper {
      overflow: auto !important;
      height: 100%;
    }
  }
}

.contact-name {
  color: var(--v-primary-base);

  @media (max-width: 960px) {
    font-size: 16px;
  }
}

.table-container {
  height: 100%;
}

.table__checkbox,
.v-data-table__checkbox {
  width: 18px;
  .v-input--selection-controls__input {
    margin-right: 0 !important;
  }
}
</style>
